import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCustomer } from "@/contexts/CustomerContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import customerApi from "@/lib/customerApi";
import api from "@/lib/api";
import SquareCardForm from "@/components/SquareCardForm";
import CardLogosRow from "@/components/CardLogosRow";
import LiveTrackDialog from "@/components/LiveTrackDialog";
import SafetySupportCard from "@/components/SafetySupportCard";
import SupportEdgeTab from "@/components/SupportEdgeTab";
import PanicButton from "@/components/PanicButton";
import PassengerPicker from "@/components/PassengerPicker";
import { toast } from "sonner";
import { fmtUsd } from "@/lib/format";
import VersionBadge from "@/components/VersionBadge";
import PaymentLogos from "@/components/PaymentLogos";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import StopsManager from "@/components/StopsManager";
import TripMap from "@/components/TripMap";
import { downloadWaybillPDF } from "@/lib/waybill";
import { formatApiError } from "@/lib/errors";
import { LogOut, MapPin, Users, Car, Loader2, Download, FileText, Plus, CreditCard, Trash2, Star, DollarSign, AlertTriangle, Phone, Lock, Sparkles, User, Menu as MenuIcon, X as XIcon, ShieldAlert, Home, Receipt, Clock } from "lucide-react";
import SubscriptionsPanel from "@/components/customer/SubscriptionsPanel";
import IncidentReportForm from "@/components/IncidentReportForm";
import RiderProfileTab from "@/components/customer/RiderProfileTab";
import { RiderLoyaltyCard, RiderFreeRideHistory } from "@/components/LoyaltyPunchCard";
import RiderPricesPanel from "@/components/customer/RiderPricesPanel";
import ModeSwitcher from "@/components/ModeSwitcher";
import QuickSwitcherCommand from "@/components/QuickSwitcherCommand";
import CustomerChatWidget from "@/components/customer/CustomerChatWidget";

/**
 * v2.6.9 batch BL · Rider hamburger menu — mirrors the Admin/Driver pattern.
 * Tap the menu icon → slide-in Sheet drawer with:
 *   · App Home (Book a Ride)
 *   · My Trips
 *   · My Cards
 *   · Profile
 *   · Subscriptions (when feature flag is on)
 *   · Safety & Support (opens nested sheet with SafetySupportCard)
 *   · Incident Report
 *   · Sign out
 * Designed for mobile-first; the full-bleed Sheet handles small viewports
 * gracefully. On desktop it slides in from the right.
 */
function CustomerHeader({ customer, logout, nav, setTab, subsEnabled }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const goTab = (t) => { setTab(t); closeMenu(); };

  // v2.8 · One-tap "Back to Driver" for staff riding as themselves — mints a
  // fresh driver session (works whether they hopped in or logged in directly).
  const [hopBusy, setHopBusy] = useState(false);
  const backToDriver = async () => {
    setHopBusy(true);
    try {
      const { data } = await customerApi.post("/customer/staff/driver-mode");
      localStorage.setItem("rtg_token", data.access_token);
      localStorage.setItem("rtg_user", JSON.stringify(data.driver));
      localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch to driver");
      setHopBusy(false);
    }
  };
  return (
    <>
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0c0d10]/80 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/buller-logo-header.png" alt="Buller Taxi" className="h-9 w-9 object-contain"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold leading-none">BULLER TAXI</div>
              <div className="text-sm font-bold leading-tight">Rider Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 hidden sm:inline">{customer?.name}</span>
            {customer?.staff && (
              <Button
                data-testid="rider-back-to-driver"
                size="sm"
                onClick={backToDriver}
                disabled={hopBusy}
                className="h-8 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-bold gap-1"
                title="Return to your driver app"
              >
                {hopBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Car className="h-3.5 w-3.5"/>}
                <span className="hidden sm:inline">Back to Driver</span>
                <span className="sm:hidden">Driver</span>
              </Button>
            )}
            <ModeSwitcher mode="rider" compact/>
            <Button
              data-testid="rider-menu-btn"
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen(true)}
              className="text-zinc-200 hover:text-amber-400 h-10 w-10"
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5"/>
            </Button>
          </div>
        </div>
      </header>

      {/* Slide-in menu drawer — mobile-first, dark theme, big tap targets */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="right"
          className="w-[85vw] sm:w-[360px] bg-[#0c0d10] border-l border-zinc-800 text-zinc-100 p-0"
          data-testid="rider-menu-drawer"
        >
          <SheetHeader className="px-5 py-4 border-b border-zinc-800">
            <SheetTitle className="text-zinc-100 flex items-center gap-2">
              <img src="/buller-taxi-crest.png" alt="" className="h-7 w-7 object-contain"/>
              <span className="text-sm font-bold">Rider Menu</span>
            </SheetTitle>
            <div className="text-[11px] text-zinc-400 truncate">{customer?.name} · {customer?.email}</div>
          </SheetHeader>
          <nav className="px-3 py-3 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 100px)" }}>
            <RiderMenuItem testid="rider-menu-book" Icon={Home} label="Book a Ride" onClick={() => goTab("book")}/>
            <RiderMenuItem testid="rider-menu-prices" Icon={Receipt} label="Prices" accent="amber" onClick={() => goTab("prices")}/>
            <RiderMenuItem testid="rider-menu-trips" Icon={FileText} label="My Trips" onClick={() => goTab("trips")}/>
            <RiderMenuItem testid="rider-menu-cards" Icon={CreditCard} label="My Cards" onClick={() => goTab("cards")}/>
            <RiderMenuItem testid="rider-menu-profile" Icon={User} label="Profile" onClick={() => goTab("profile")}/>
            {subsEnabled && (
              <RiderMenuItem testid="rider-menu-subscriptions" Icon={Sparkles} label="Subscriptions" accent="amber" onClick={() => goTab("subscriptions")}/>
            )}
            <RiderMenuItem
              testid="rider-menu-safety-support"
              Icon={ShieldAlert}
              label="Safety & Support"
              accent="red"
              onClick={() => { setSupportOpen(true); closeMenu(); }}
            />
            <RiderMenuItem testid="rider-menu-incident" Icon={AlertTriangle} label="Incident Report" accent="red" onClick={() => goTab("incident")}/>
            <div className="pt-2 mt-2 border-t border-zinc-800">
              <RiderMenuItem
                testid="rider-menu-logout"
                Icon={LogOut}
                label="Sign out"
                danger
                onClick={() => { closeMenu(); logout(); nav("/login"); }}
              />
            </div>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Nested Safety & Support sheet — shows the SafetySupportCard at full
          height so the rider can call dispatch / mail support / mail safety
          from one tap without leaving the dashboard. */}
      <Sheet open={supportOpen} onOpenChange={setSupportOpen}>
        <SheetContent
          side="right"
          className="w-[90vw] sm:w-[400px] bg-[#0c0d10] border-l border-red-500/40 text-zinc-100 p-0"
          data-testid="rider-safety-support-sheet"
        >
          <SheetHeader className="px-5 py-4 border-b border-zinc-800 bg-[#121212]">
            <SheetTitle className="text-zinc-100 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-red-400"/>
              <span className="text-sm font-bold uppercase tracking-wider">Safety & Support</span>
            </SheetTitle>
          </SheetHeader>
          <div className="p-4">
            <SafetySupportCard testid="rider-safety-support-card" title="Reach Us"/>
            <p className="text-[10px] text-zinc-500 text-center mt-3 uppercase tracking-widest">
              In an emergency, use the red SOS tab on the right edge.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function RiderMenuItem({ Icon, label, onClick, accent, danger, testid }) {
  const cls = danger
    ? "bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20"
    : accent === "red"
      ? "border-red-500/30 text-red-200 hover:bg-red-500/10"
      : accent === "amber"
        ? "border-amber-400/30 text-amber-200 hover:bg-amber-400/10"
        : "border-zinc-800 text-zinc-100 hover:bg-zinc-900";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-md border text-sm font-medium transition-colors ${cls}`}
    >
      <Icon className="h-4 w-4 flex-shrink-0"/>
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

export default function CustomerDashboard() {
  const { customer, logout, refresh } = useCustomer();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => { if (!customer) nav("/ride/login"); }, [customer, nav]);

  const [tab, setTab] = useState("book");
  const [rides, setRides] = useState([]);
  const [cards, setCards] = useState([]);
  const [availability, setAvailability] = useState(null);
  const reload = () => customerApi.get("/rides").then(r => setRides(r.data)).catch((err) => console.warn("rides reload failed", err?.response?.status));
  const reloadCards = () => customerApi.get("/customer/cards").then(r => setCards(r.data)).catch((err) => console.warn("cards reload failed", err?.response?.status));
  const reloadAvailability = () => api.get("/dispatch/availability").then(r => setAvailability(r.data)).catch((err) => console.warn("availability reload failed", err?.response?.status));
  // v2.6.8 batch P-4 · admin can disable the entire Subscriptions feature
  // from Admin → Payments. We poll once on mount + bounce the rider back to
  // the Book tab if they happen to be sitting on Subscriptions when it flips.
  const [subsEnabled, setSubsEnabled] = useState(true);
  useEffect(() => {
    api.get("/subscriptions/plans")
      .then(r => setSubsEnabled(r.data?.enabled !== false))
      .catch((err) => console.warn("subscriptions feature flag fetch failed", err?.response?.status));
  }, []);
  useEffect(() => { if (!subsEnabled && tab === "subscriptions") setTab("book"); }, [subsEnabled, tab]);
  useEffect(() => { if (customer) { reload(); reloadCards(); reloadAvailability(); } }, [customer]);
  // Re-poll availability every 20s while on the dashboard so the rider sees driver-online changes live
  useEffect(() => {
    if (!customer) return;
    const t = setInterval(reloadAvailability, 20000);
    return () => clearInterval(t);
  }, [customer]);

  if (!customer) return null;

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 fade-in">
      <QuickSwitcherCommand mode="rider"/>
      <CustomerHeader customer={customer} logout={logout} nav={nav} setTab={setTab} subsEnabled={subsEnabled}/>
      <main className="max-w-4xl mx-auto px-5 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`bg-zinc-900 border border-zinc-800 grid ${subsEnabled ? "grid-cols-3 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-5"} mb-5 h-auto gap-1 p-1`}>
            <TabsTrigger value="book" data-testid="rider-tab-book" className="text-xs sm:text-sm py-2"><Plus className="h-4 w-4 mr-1"/>Book a Ride</TabsTrigger>
            {subsEnabled && <TabsTrigger value="subscriptions" data-testid="rider-tab-subscriptions" className="text-xs sm:text-sm py-2 text-amber-300 data-[state=active]:text-amber-200"><Sparkles className="h-4 w-4 mr-1"/>Subscriptions</TabsTrigger>}
            <TabsTrigger value="trips" data-testid="rider-tab-trips" className="text-xs sm:text-sm py-2"><FileText className="h-4 w-4 mr-1"/>My Trips ({rides.length})</TabsTrigger>
            <TabsTrigger value="cards" data-testid="rider-tab-cards" className="text-xs sm:text-sm py-2"><CreditCard className="h-4 w-4 mr-1"/>My Cards ({cards.length})</TabsTrigger>
            <TabsTrigger value="profile" data-testid="rider-tab-profile" className="text-xs sm:text-sm py-2"><User className="h-4 w-4 mr-1"/>Profile</TabsTrigger>
            {/* v2.6.9 batch Q · rider-side Incident Report tab. Same component
                as the driver page; flipped into rider mode (no driver-kind
                dropdown, "Rider's Name" pre-fill). */}
            <TabsTrigger value="incident" data-testid="rider-tab-incident" className="text-xs sm:text-sm py-2 text-red-300 data-[state=active]:text-red-200"><AlertTriangle className="h-4 w-4 mr-1"/>Incident</TabsTrigger>
          </TabsList>
          <TabsContent value="book"><RiderFreeRideHistory/><BookRide onBooked={reload} customer={customer}/><RiderLoyaltyCard compact name={customer?.name || customer?.first_name || "Rider"}/></TabsContent>
          {/* v2.6.9 batch BN · "Prices" tab — reached via the rider menu drawer
              (not surfaced as a top-tab trigger to keep the row uncluttered). */}
          <TabsContent value="prices"><RiderPricesPanel onBookRide={setTab}/></TabsContent>
          {subsEnabled && <TabsContent value="subscriptions"><SubscriptionsPanel/></TabsContent>}
          <TabsContent value="trips"><MyTrips rides={rides}/></TabsContent>
          <TabsContent value="cards"><MyCards cards={cards} reload={reloadCards}/></TabsContent>
          <TabsContent value="profile"><RiderProfileTab customer={customer} onSaved={refresh}/></TabsContent>
          <TabsContent value="incident"><RiderIncidentTab customer={customer}/></TabsContent>
        </Tabs>

        <PaymentLogos className="mt-8"/>
        <div className="mt-6 pt-4 border-t border-zinc-800 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-zinc-500" data-testid="legal-footer">
          <a href="/legal/privacy" className="hover:text-amber-400">Privacy</a>
          <span className="text-zinc-700">·</span>
          <a href="/legal/terms" className="hover:text-amber-400">Terms</a>
          <span className="text-zinc-700">·</span>
          <a href="/legal/refund" className="hover:text-amber-400">Refund</a>
          <span className="text-zinc-700">·</span>
          <a href="/legal/faq" className="hover:text-amber-400">FAQ</a>
          <span className="text-zinc-700">·</span>
          <button
            data-testid="request-deactivation-btn"
            onClick={async () => {
              if (!window.confirm("Request account deactivation?\n\nYou will no longer be able to book rides. An admin can reactivate your account on request.")) return;
              try {
                await customerApi.post("/customer/me/request-deactivation");
                toast.success("Deactivation requested · signing you out");
                logout(); nav("/login");
              } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
            }}
            className="hover:text-red-400"
          >Request Deactivation</button>
        </div>
      </main>
      <VersionBadge/>
      {/* v2.6.9 batch BL · Floating Safety & Support widgets — same right-edge
          stack the driver gets. PanicButton (red, 3-sec hold) + SupportEdgeTab
          (indigo, collapsible support contacts). Pinned above the GPS map area. */}
      <PanicButton/>
      <SupportEdgeTab/>
      <CustomerChatWidget/>
    </div>
  );
}

function BookRide({ onBooked, customer }) {
  // v2.7.65+E254 · Uber-style simplified rider booking. REQUIRED: pickup,
  // drop-off, requested time, passengers. Everything else is optional. The
  // booking is submitted to the SAME dispatch pipeline as the public/dispatch
  // quick-booking (/site/quick-booking, source="rider_account") so it lands
  // in the Ride Coordinator as a Pending ride for a dispatcher to assign.
  const homeAddr = (customer?.address || "").trim();
  const [origin, setOrigin] = useState(homeAddr);
  const [destination, setDestination] = useState("");
  const [pax, setPax] = useState(1);
  const [rideType, setRideType] = useState("single");
  const [when, setWhen] = useState("now"); // "now" | "schedule"
  const [scheduleAt, setScheduleAt] = useState("");
  const [payPref, setPayPref] = useState("pickup"); // pickup | card | cash | cashapp
  const [note, setNote] = useState("");
  const [stops, setStops] = useState([]);   // extra-stop addresses ($5 each)
  const [wfd, setWfd] = useState(null);      // Weatherford in-city group estimate
  const [wfdBusy, setWfdBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estBusy, setEstBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);
  const { refresh: refreshCustomer } = useCustomer();
  const [savingPlace, setSavingPlace] = useState(false);
  const [placeLabel, setPlaceLabel] = useState("");

  // v2.8+E301 · Rider Portal Hours of Operation. The form is active only
  // during operating hours (default 10:00 AM – 3:00 AM CT, 7 days a week).
  // Outside hours it renders as INACTIVE behind a message overlay AND the
  // server rejects the submit. Fetch on mount, refresh on a 20s ticker (so the
  // scheduled 10AM/3AM flip happens automatically), and subscribe to a WS push
  // so admin changes reflect in real time.
  const [hours, setHours] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetchHours = () => api.get("/rider-portal/hours")
      .then((r) => { if (alive) setHours(r.data); })
      .catch(() => {});
    fetchHours();
    const poll = setInterval(fetchHours, 20000);
    let ws;
    try {
      const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      ws = new WebSocket(`${wsBase}/api/ws/rider-hours`);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg && msg.type === "rider_portal_hours_updated") setHours(msg);
        } catch { /* ignore malformed frame */ }
      };
    } catch { /* WS optional — the 20s poll still covers it */ }
    return () => { alive = false; clearInterval(poll); try { ws && ws.close(); } catch { /* noop */ } };
  }, []);
  const portalClosed = !!(hours && hours.is_open === false);


  // Pre-fill pickup with the rider's saved home address once it loads.
  const [pickupTouched, setPickupTouched] = useState(false);
  useEffect(() => {
    if (!pickupTouched && homeAddr && !origin) setOrigin(homeAddr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeAddr]);

  // Informational fare estimate (debounced) — never blocks booking.
  useEffect(() => {
    if (!origin || !destination) { setEstimate(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setEstBusy(true);
      try {
        const { data } = await customerApi.post("/customer/quote", {
          ride_type: rideType, origin, destination, passengers: pax, stops: [],
        });
        if (!cancelled) setEstimate({ total: data.subtotal, miles: data.miles, minutes: data.minutes });
      } catch { if (!cancelled) setEstimate(null); }
      finally { if (!cancelled) setEstBusy(false); }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [origin, destination, pax, rideType]);

  // v2.8+E283 · Weatherford in-city group pricing estimate (debounced).
  // Applies only when BOTH pickup + destination are inside the city limits;
  // otherwise dispatch quotes the fare manually.
  useEffect(() => {
    if (!origin.trim() || !destination.trim()) { setWfd(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setWfdBusy(true);
      try {
        const { data } = await customerApi.post("/customer/weatherford-estimate", {
          origin: origin.trim(), destination: destination.trim(),
          passengers: pax, extra_stops: stops.length,
        });
        if (!cancelled) setWfd(data);
      } catch { if (!cancelled) setWfd(null); }
      finally { if (!cancelled) setWfdBusy(false); }
    }, 750);
    return () => { cancelled = true; clearTimeout(t); };
  }, [origin, destination, pax, stops.length]);

  const minSchedule = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);
  const canBook = !!(origin.trim() && destination.trim() && (when === "now" || scheduleAt) && !busy && !portalClosed);

  const submit = async () => {
    if (portalClosed) return toast.error(hours?.closed_message || "Online booking is currently closed.");
    if (!origin.trim() || !destination.trim()) return toast.error("Enter both pickup and drop-off");
    if (when === "schedule" && !scheduleAt) return toast.error("Pick a date & time, or choose Now");
    setBusy(true);
    try {
      const payLabel = { pickup: "Pay driver at pickup", card: "Card", cash: "Cash", cashapp: "Cash App" }[payPref];
      const notes = [
        `Payment preference: ${payLabel}`,
        note.trim() ? `Rider note: ${note.trim()}` : "",
      ].filter(Boolean).join(" · ");
      const riderName = (customer?.name || `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim() || "Rider");
      const { data } = await api.post("/site/quick-booking", {
        name: riderName,
        phone: customer?.phone || "",
        email: customer?.email || null,
        origin: origin.trim(),
        destination: destination.trim(),
        passengers: pax,
        ride_type: rideType,
        home_address: homeAddr || origin.trim(),
        schedule_mode: when === "schedule" ? "scheduled" : "asap",
        scheduled_for: when === "schedule" ? new Date(scheduleAt).toISOString() : null,
        stops: stops.map((s) => s.trim()).filter(Boolean),
        source: "rider_account",
        notes,
        preferred_driver_gender: customer?.preferred_driver_gender || "any",
      });
      // v2.8+E283 · Stamp the authoritative Weatherford in-city fare + true
      // rider count + dispatch notes onto the queue card so the Ride
      // Coordinator shows the price automatically. Non-blocking.
      let finalTotal = null;
      if (data?.trip_id) {
        try {
          const pr = await customerApi.post(`/customer/bookings/${data.trip_id}/pricing`, {
            origin: origin.trim(), destination: destination.trim(),
            passengers: pax, extra_stops: stops.length,
            stop_addresses: stops.map((s) => s.trim()).filter(Boolean),
            group_ride: rideType === "group",
          });
          if (pr?.data?.applied) finalTotal = pr.data.total;
        } catch { /* non-blocking — dispatch will quote */ }
      }
      setBooked({
        trip_id: data?.trip_id, status: data?.status,
        origin: origin.trim(), destination: destination.trim(),
        passengers: pax, when, scheduleAt, rideType,
        total: finalTotal,
      });
      toast.success("Ride requested · dispatch will assign a driver");
      onBooked?.();
    } catch (e) {
      toast.error(formatApiError(e, "Could not submit your booking"), { duration: 8000 });
    } finally { setBusy(false); }
  };

  const savePlace = async () => {
    const label = placeLabel.trim();
    const address = destination.trim();
    if (!label || !address) return;
    try {
      await customerApi.post("/customer/saved-places", { label, address });
      await refreshCustomer?.();
      toast.success(`Saved "${label}"`);
      setSavingPlace(false); setPlaceLabel("");
    } catch (e) {
      toast.error(formatApiError(e, "Could not save place"));
    }
  };

  if (booked) return (
    <Card className="bg-zinc-900 border-amber-400/40 p-6 fade-in" data-testid="rider-book-success">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400">Ride Requested</div>
          <div className="meter-display text-2xl font-bold mt-1" data-testid="rider-book-ref">{booked.trip_id || "Received"}</div>
        </div>
        <Badge variant="outline" className="border-green-400/40 text-green-400">{(booked.status || "Pending Dispatch").toUpperCase()}</Badge>
      </div>
      <div className="mt-4 bg-zinc-950 border border-zinc-800 rounded p-4 space-y-1.5 text-sm">
        <div className="flex justify-between gap-3"><span className="text-zinc-400">Pickup</span><span className="font-semibold truncate text-right">{booked.origin}</span></div>
        <div className="flex justify-between gap-3"><span className="text-zinc-400">Drop-off</span><span className="font-semibold truncate text-right">{booked.destination}</span></div>
        <div className="flex justify-between"><span className="text-zinc-400">When</span><span className="font-semibold">{booked.when === "schedule" ? new Date(booked.scheduleAt).toLocaleString() : "Now (ASAP)"}</span></div>
        <div className="flex justify-between"><span className="text-zinc-400">Passengers</span><span className="meter-display">{booked.passengers}</span></div>
        {booked.total != null && (
          <div className="flex justify-between" data-testid="rider-book-fare"><span className="text-zinc-400">Fare</span><span className="meter-display text-amber-400 font-bold">{fmtUsd(booked.total)}</span></div>
        )}
      </div>
      <p className="text-xs text-zinc-400 mt-3 leading-relaxed">
        Our dispatch team has your request and will assign a driver. We'll text you at
        <span className="text-amber-400 font-semibold"> {customer?.phone}</span> when a driver is on the way.
      </p>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <Button data-testid="rider-book-another" onClick={() => { setBooked(null); setDestination(""); setNote(""); setWhen("now"); setScheduleAt(""); setStops([]); }} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">Book Another</Button>
        <a href={`tel:${(customer?.phone || "").replace(/[^0-9+]/g, "") || "+15802194687"}`} className="hidden"/>
        <Button variant="outline" onClick={() => { setBooked(null); }} className="bg-zinc-950 border-zinc-700">Done</Button>
      </div>
    </Card>
  );

  return (
    <div className="relative" data-testid="rider-book-wrap">
      {portalClosed && (
        <div className="absolute inset-0 z-20 flex items-start justify-center p-3 sm:p-6" data-testid="rider-book-closed">
          <Card className="bg-zinc-900 border-rose-500/50 p-6 max-w-md w-full text-center shadow-2xl">
            <div className="mx-auto h-12 w-12 rounded-full bg-rose-500/10 border border-rose-500/40 flex items-center justify-center mb-3">
              <Clock className="h-6 w-6 text-rose-400" />
            </div>
            <h3 className="text-lg font-bold text-rose-300 mb-1">Online booking is closed</h3>
            <p className="text-sm text-zinc-300">You're unable to book a ride at this time.</p>
            <div className="mt-4 rounded-lg bg-zinc-950 border border-zinc-800 p-3 text-sm">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Booking hours</div>
              <div className="text-zinc-100 font-bold" data-testid="rider-book-closed-hours">{hours?.hours_label}</div>
              <div className="text-zinc-400 text-xs">{hours?.days_label}</div>
            </div>
            {hours?.next_open_label && (
              <p className="text-xs text-emerald-300 mt-3" data-testid="rider-book-closed-next">Booking reopens {hours.next_open_label}</p>
            )}
            <p className="text-[11px] text-zinc-500 mt-3">This form reopens automatically at opening time. Need a ride sooner? Please call dispatch.</p>
          </Card>
        </div>
      )}
      <div className={portalClosed ? "opacity-40 pointer-events-none select-none" : ""} aria-hidden={portalClosed}>
    <Card className="bg-zinc-900 border-zinc-800 p-5 fade-in" data-testid="rider-book-form">
      <h2 className="text-lg font-bold mb-1">Book a Ride</h2>
      <p className="text-xs text-zinc-400 mb-4">Fast &amp; simple — just tell us where and when. A dispatcher assigns your driver.</p>

      {/* Ride type — optional, defaults to Single */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button data-testid="rt-single" onClick={() => setRideType("single")} className={`p-3 rounded-xl border text-left transition-colors ${rideType === "single" ? "bg-amber-400/10 border-amber-400" : "bg-zinc-950 border-zinc-800"}`}>
          <Car className="h-5 w-5 text-amber-400 mb-1"/>
          <div className="text-sm font-bold">Single Fare</div>
          <div className="text-[10px] text-zinc-500">Private trip</div>
        </button>
        <button data-testid="rt-group" onClick={() => setRideType("group")} className={`p-3 rounded-xl border text-left transition-colors ${rideType === "group" ? "bg-amber-400/10 border-amber-400" : "bg-zinc-950 border-zinc-800"}`}>
          <Users className="h-5 w-5 text-amber-400 mb-1"/>
          <div className="text-sm font-bold">Group Ride</div>
          <div className="text-[10px] text-zinc-500">Per-passenger pricing</div>
        </button>
      </div>

      {/* Pickup + Drop-off */}
      <div className="space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-amber-400"/>Pickup</Label>
          <div className="mt-1"><AddressAutocomplete value={origin} onChange={(v) => { setPickupTouched(true); setOrigin(v); }} placeholder="Pickup address" testid="rider-origin"/></div>
          {homeAddr && origin === homeAddr && <div className="text-[10px] text-amber-400/80 mt-1">Using your saved home address · tap to change</div>}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-zinc-400"/>Drop-off</Label>
          <div className="mt-1"><AddressAutocomplete value={destination} onChange={setDestination} placeholder="Where to?" testid="rider-destination"/></div>
        </div>
        {/* v2.7.65+E255 · Saved Places — one-tap drop-off */}
        {(() => {
          const places = customer?.saved_places || [];
          const chips = [
            ...(homeAddr ? [{ id: "__home", label: "Home", address: homeAddr, home: true }] : []),
            ...places,
          ];
          const alreadySaved = chips.some((c) => (c.address || "").trim() === destination.trim());
          if (chips.length === 0 && !destination.trim()) return null;
          return (
            <div data-testid="rider-saved-places">
              <div className="flex flex-wrap items-center gap-2">
                {chips.map((pl) => (
                  <button
                    key={pl.id}
                    data-testid={`saved-place-chip-${pl.id}`}
                    onClick={() => setDestination(pl.address)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-950 text-xs font-semibold text-zinc-200 hover:border-amber-400 hover:text-amber-400 transition-colors max-w-[190px]"
                  >
                    {pl.home ? <Home className="h-3.5 w-3.5 text-amber-400 shrink-0"/> : <Star className="h-3.5 w-3.5 text-amber-400 shrink-0"/>}
                    <span className="truncate">{pl.label}</span>
                  </button>
                ))}
                {destination.trim() && !alreadySaved && !savingPlace && (
                  <button
                    data-testid="saved-place-save-btn"
                    onClick={() => setSavingPlace(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-amber-400/50 text-xs font-semibold text-amber-400 hover:bg-amber-400/10 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5"/>Save this place
                  </button>
                )}
              </div>
              {savingPlace && (
                <div className="flex items-center gap-2 mt-2" data-testid="saved-place-form">
                  <Input autoFocus value={placeLabel} onChange={(e) => setPlaceLabel(e.target.value)} placeholder="Label (e.g. Work, Mom's)" className="bg-zinc-950 border-zinc-700 h-10 text-sm flex-1" data-testid="saved-place-label"/>
                  <Button onClick={savePlace} disabled={!placeLabel.trim()} className="h-10 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold px-4" data-testid="saved-place-confirm">Save</Button>
                  <Button variant="outline" onClick={() => { setSavingPlace(false); setPlaceLabel(""); }} className="h-10 w-10 bg-zinc-950 border-zinc-700 p-0" data-testid="saved-place-cancel"><XIcon className="h-4 w-4"/></Button>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* When */}
      <div className="mt-4">
        <Label className="text-xs uppercase tracking-widest text-zinc-400">When do you need it?</Label>
        <div className="grid grid-cols-2 gap-2 mt-1.5">
          <button data-testid="rider-when-now" onClick={() => setWhen("now")} className={`h-14 rounded-xl border font-bold text-sm transition-colors ${when === "now" ? "bg-amber-400 text-zinc-950 border-amber-400" : "bg-zinc-950 border-zinc-800 text-zinc-200"}`}>Now (ASAP)</button>
          <button data-testid="rider-when-schedule" onClick={() => setWhen("schedule")} className={`h-14 rounded-xl border font-bold text-sm transition-colors ${when === "schedule" ? "bg-amber-400 text-zinc-950 border-amber-400" : "bg-zinc-950 border-zinc-800 text-zinc-200"}`}>Schedule for later</button>
        </div>
        {when === "schedule" && (
          <div className="mt-2">
            <Input
              data-testid="rider-schedule-at"
              type="datetime-local"
              value={scheduleAt}
              min={minSchedule}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="bg-zinc-950 border-zinc-700 h-12 meter-display"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Trips booked 24h+ in advance are handled as scheduled Future Fares.</p>
          </div>
        )}
      </div>

      {/* Passengers */}
      <div className="mt-4">
        <PassengerPicker value={pax} onChange={setPax} label="Riders in your group" testid="rider-pax" min={1} max={15}/>
      </div>

      {/* Extra stops ($5 each) */}
      <div className="mt-4" data-testid="rider-stops">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-amber-400"/>Extra stops</Label>
          <span className="text-[10px] text-zinc-500">$5.00 each</span>
        </div>
        <div className="space-y-2 mt-1.5">
          {stops.map((s, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`rider-stop-row-${i}`}>
              <div className="flex-1 min-w-0">
                <AddressAutocomplete value={s} onChange={(v) => setStops((arr) => arr.map((x, j) => (j === i ? v : x)))} placeholder={`Stop ${i + 1} (store, extra pickup, etc.)`} testid={`rider-stop-${i}`}/>
              </div>
              <Button variant="outline" onClick={() => setStops((arr) => arr.filter((_, j) => j !== i))} className="h-11 w-11 bg-zinc-950 border-zinc-700 p-0 shrink-0" data-testid={`rider-stop-remove-${i}`}><XIcon className="h-4 w-4"/></Button>
            </div>
          ))}
          {stops.length < 5 && (
            <button data-testid="rider-stop-add" onClick={() => setStops((arr) => [...arr, ""])} className="w-full h-11 rounded-lg border border-dashed border-amber-400/50 text-xs font-semibold text-amber-400 hover:bg-amber-400/10 transition-colors flex items-center justify-center gap-1">
              <Plus className="h-3.5 w-3.5"/>Add a stop (+$5.00)
            </button>
          )}
        </div>
      </div>

      {/* 10+ riders group alert */}
      {pax >= 10 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 text-xs text-amber-300" data-testid="rider-group-alert">
          <Users className="h-4 w-4 shrink-0 mt-0.5"/>
          <span>Large group of {pax}. Dispatch may send <b>multiple taxis</b> — we'll coordinate that for you.</span>
        </div>
      )}

      {/* Live route preview */}
      {origin && destination && <TripMap origin={origin} destination={destination} stops={stops.map((s) => s.trim()).filter(Boolean)} height={220}/>}

      {/* Weatherford in-city group rate (authoritative) — else generic estimate */}
      {wfd?.applies ? (
        <div className="mt-4 p-4 rounded-xl bg-zinc-950 border border-amber-400/60" data-testid="rider-wfd-estimate">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400/90 flex items-center gap-1"><MapPin className="h-3 w-3"/>Weatherford In-City Fare</div>
              <div className="meter-display text-3xl font-extrabold text-amber-400 mt-0.5" data-testid="rider-wfd-total">{fmtUsd(wfd.total)}</div>
            </div>
            <div className="text-right text-[10px] text-zinc-500 space-y-0.5">
              {(wfd.breakdown || []).map((b, i) => (
                <div key={i}>{b.label}: <span className="text-zinc-300">{fmtUsd(b.amount)}</span></div>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">Flat in-city rate · dispatch can adjust before your driver arrives.</div>
        </div>
      ) : (estimate || estBusy) ? (
        <div className="mt-4 p-4 rounded-xl bg-zinc-950 border border-amber-400/40 flex items-center justify-between" data-testid="rider-estimate">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Estimated Fare</div>
            {estBusy ? (
              <div className="text-zinc-400 text-sm mt-1 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Calculating…</div>
            ) : (
              <div className="meter-display text-3xl font-extrabold text-amber-400 mt-0.5" data-testid="rider-estimate-total">{fmtUsd(estimate.total)}</div>
            )}
          </div>
          {estimate && !estBusy && <div className="text-[10px] text-zinc-500 text-right uppercase">{estimate.miles} mi<br/>{estimate.minutes} min</div>}
        </div>
      ) : null}
      {wfd && !wfd.applies && origin.trim() && destination.trim() && !wfdBusy && (
        <div className="text-[10px] text-zinc-500 mt-1.5 text-center" data-testid="rider-wfd-outside">Outside Weatherford city limits · dispatch confirms your final fare.</div>
      )}
      {estimate && !estBusy && !wfd?.applies && <div className="text-[10px] text-zinc-500 mt-1.5 text-center">Estimate only · dispatch confirms the final fare when your driver is assigned.</div>}

      {/* Optional extras */}
      <button data-testid="rider-more-toggle" onClick={() => setShowMore((v) => !v)} className="w-full mt-4 text-xs text-amber-400 hover:text-amber-300 font-semibold uppercase tracking-wider flex items-center justify-center gap-1">
        {showMore ? "Hide options" : "More options (payment, notes)"}
      </button>
      {showMore && (
        <div className="mt-3 space-y-3 fade-in">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400">How would you like to pay? (optional)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
              {[
                { id: "pickup", label: "At Pickup" },
                { id: "card", label: "Card" },
                { id: "cash", label: "Cash" },
                { id: "cashapp", label: "Cash App" },
              ].map((p) => (
                <button key={p.id} data-testid={`rider-pay-${p.id}`} onClick={() => setPayPref(p.id)} className={`h-11 rounded-lg border text-xs font-bold transition-colors ${payPref === p.id ? "bg-amber-400/10 border-amber-400 text-amber-400" : "bg-zinc-950 border-zinc-700 text-zinc-300"}`}>{p.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Note for the driver (optional)</Label>
            <Input data-testid="rider-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Ring the bell, blue house" className="bg-zinc-950 border-zinc-700 h-11 mt-1"/>
          </div>
        </div>
      )}

      <Button data-testid="rider-book-submit" onClick={submit} disabled={!canBook} className="w-full mt-5 h-14 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold text-base rounded-xl disabled:opacity-50">
        {busy ? <Loader2 className="h-5 w-5 animate-spin"/> : (when === "schedule" ? "SCHEDULE RIDE" : "BOOK RIDE")}
      </Button>
      <p className="text-[10px] text-zinc-500 mt-2 text-center">No card required to book — you can pay your driver at pickup.</p>
    </Card>
      </div>
    </div>
  );
}
function MyTrips({ rides }) {
  if (rides.length === 0) return <Card className="bg-zinc-900 border-zinc-800 p-8 text-center text-zinc-500 text-sm">No trips yet.</Card>;
  const liveStatuses = new Set(["accepted", "in_progress", "dispatching"]);
  return (
    <div className="space-y-2">
      {rides.map(r => (
        <Card key={r.id} className="bg-zinc-900 border-zinc-800 p-4 flex items-start justify-between gap-3" data-testid={`mytrip-${r.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-amber-400/40 text-amber-400 text-[10px] uppercase">{r.ride_type}</Badge>
              {liveStatuses.has(r.status) && <Badge variant="outline" className="text-[10px] uppercase border-green-400/40 text-green-400">{r.status.replace("_"," ")}</Badge>}
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{r.waybill_no}</span>
            </div>
            <div className="text-sm font-semibold mt-1 truncate">{r.origin} → {r.destination}</div>
            <div className="text-[11px] text-zinc-500">{new Date(r.created_at).toLocaleString()} · {r.passengers} pax</div>
          </div>
          <div className="text-right flex-shrink-0 space-y-1">
            <div className="meter-display text-lg font-bold text-amber-400">{fmtUsd(r.total)}</div>
            {liveStatuses.has(r.status) && <LiveTrackDialog rideId={r.id} apiClient={customerApi} scope="customer" buttonLabel="Live"/>}
            <Button size="sm" variant="ghost" onClick={() => downloadWaybillPDF(r)} className="text-amber-400 text-xs h-7" data-testid={`dl-${r.id}`}><Download className="h-3 w-3 mr-1"/>Waybill</Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function MyCards({ cards, reload }) {
  const [adding, setAdding] = useState(false);
  const [showSquareForm, setShowSquareForm] = useState(true);
  const [num, setNum] = useState("");
  const [mm, setMm] = useState("");
  const [yy, setYy] = useState("");
  const [cvc, setCvc] = useState("");
  const [zip, setZip] = useState("");
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setNum(""); setMm(""); setYy(""); setCvc(""); setZip(""); setName(""); setLabel(""); setIsDefault(false); setAdding(false); };

  const submitFromSquareToken = async (token, details) => {
    setBusy(true);
    try {
      const exp = (details?.card?.expMonth && details?.card?.expYear) ? { exp_month: details.card.expMonth, exp_year: details.card.expYear } : { exp_month: 12, exp_year: new Date().getFullYear() + 4 };
      await customerApi.post("/customer/cards", {
        number: details?.card?.last4 ? `4242424242${details.card.last4}` : "4242424242424242", // Luhn-OK placeholder; real last4 from token
        ...exp,
        billing_zip: details?.billing?.postalCode || undefined,
        cardholder_name: name || undefined,
        label: label || `${(details?.card?.brand || "CARD").toUpperCase()} •••• ${details?.card?.last4 || ""}`,
        is_default: isDefault, token,
      });
      toast.success("Card saved & tokenized via Square");
      reset(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save card"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    const exp_month = parseInt(mm, 10);
    let exp_year = parseInt(yy, 10);
    if (Number.isNaN(exp_month) || Number.isNaN(exp_year)) return toast.error("Enter a valid expiry");
    if (exp_year < 100) exp_year += 2000;
    setBusy(true);
    try {
      await customerApi.post("/customer/cards", {
        number: num, exp_month, exp_year,
        cvc: cvc || undefined, billing_zip: zip || undefined,
        cardholder_name: name || undefined, label: label || undefined,
        is_default: isDefault,
      });
      toast.success("Card saved");
      reset();
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save card"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this card?")) return;
    try { await customerApi.delete(`/customer/cards/${id}`); toast.success("Card removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const makeDefault = async (id) => {
    try { await customerApi.patch(`/customer/cards/${id}/default`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="space-y-3" data-testid="my-cards-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Saved Cards</h2>
            <p className="text-xs text-zinc-400 mt-0.5">Stored securely. Only the last 4 digits and expiry are kept on our servers — your full card number and CVC are never stored.</p>
          </div>
          {!adding && (
            <Button data-testid="add-card-btn" onClick={()=>setAdding(true)} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold flex-shrink-0"><Plus className="h-4 w-4 mr-1"/>Add Card</Button>
          )}
        </div>

        {adding && (
          <div className="mt-4 space-y-4" data-testid="add-card-form">
            <div className="p-3 rounded-lg bg-zinc-950 border border-amber-400/30">
              <div className="text-[11px] uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1.5"><Lock className="h-3 w-3"/>Add Your Credit Card Securely</div>
              <CardLogosRow/>
              <div className="grid grid-cols-2 gap-2 my-3">
                <div className="col-span-2"><Label className="text-[10px] uppercase tracking-widest text-zinc-400">Cardholder Name</Label><Input data-testid="card-name" value={name} onChange={e=>setName(e.target.value)} placeholder="As shown on card" className="bg-zinc-900 border-zinc-700 h-10 mt-1"/></div>
                <div><Label className="text-[10px] uppercase tracking-widest text-zinc-400">Label (optional)</Label><Input data-testid="card-label" value={label} onChange={e=>setLabel(e.target.value)} placeholder="Personal" className="bg-zinc-900 border-zinc-700 h-10 mt-1"/></div>
                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer self-end pb-2">
                  <Checkbox data-testid="card-default" checked={isDefault} onCheckedChange={v=>setIsDefault(!!v)}/>Default
                </label>
              </div>
              <SquareCardForm onToken={submitFromSquareToken} busy={busy} label="SAVE CARD"/>
              <Button onClick={reset} variant="ghost" size="sm" className="w-full text-zinc-500 mt-2 text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </Card>

      {cards.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-8 text-center text-zinc-500 text-sm">No saved cards yet.</Card>
      ) : (
        <div className="space-y-2">
          {cards.map(card => (
            <Card key={card.id} className="bg-zinc-900 border-zinc-800 p-4 flex items-center justify-between gap-3" data-testid={`saved-card-${card.last4}`}>
              <div className="flex items-center gap-3 min-w-0">
                <CreditCard className="h-6 w-6 text-amber-400"/>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold uppercase">{card.brand} •••• {card.last4}</span>
                    {card.is_default && <Badge variant="outline" className="border-amber-400/60 text-amber-400 text-[9px] uppercase">Default</Badge>}
                  </div>
                  <div className="text-[11px] text-zinc-500">{card.label} · exp {String(card.exp_month).padStart(2,"0")}/{String(card.exp_year).slice(-2)}{card.cardholder_name ? ` · ${card.cardholder_name}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {!card.is_default && (
                  <Button size="sm" variant="ghost" onClick={()=>makeDefault(card.id)} className="text-amber-400 h-8" data-testid={`default-card-${card.last4}`} title="Make default"><Star className="h-4 w-4"/></Button>
                )}
                <Button size="sm" variant="ghost" onClick={()=>remove(card.id)} className="text-red-400 hover:text-red-300 h-8" data-testid={`delete-card-${card.last4}`} title="Remove"><Trash2 className="h-4 w-4"/></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// v2.6.9 batch Q · Rider-side Incident Report tab. Reuses the shared
// IncidentReportForm component in `submitterType="rider"` mode (hides the
// driver-kind dropdown, swaps the title and submitter-name label).
function RiderIncidentTab({ customer }) {
  const [history, setHistory] = useState([]);
  const reload = () => customerApi.get("/customer/incidents/mine")
    .then(r => setHistory(r.data.items || []))
    .catch((e) => console.warn("rider incident history failed", e?.response?.status));
  useEffect(() => { reload(); }, []);
  return (
    <div className="space-y-5">
      <IncidentReportForm
        submitterType="rider"
        submitterName={customer?.name}
        apiClient={customerApi}
        submitEndpoint="/customer/incidents"
        onSubmitted={reload}
      />
      {history.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="rider-incident-history">
          <h3 className="font-bold text-sm mb-3 text-amber-400">Your past reports</h3>
          <div className="space-y-2">
            {history.map((i) => (
              <div key={i.id} className="flex items-center gap-2 border border-zinc-800 rounded p-3" data-testid={`rider-incident-history-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{i.incident_number}</div>
                  <div className="text-[10px] text-zinc-500">{i.incident_date} {i.incident_time}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300 font-bold">{i.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
